import { Button, Card, CardBody, RadioGroup, Radio } from "@heroui/react";
import { useCallback, useEffect, useState } from "react";
import { api } from "@/api";
import { LinkedDevice } from "@/api/devices";
import { toast } from "react-toastify";
import useErrorHandling from "@/hooks/useErrorHandling";
import { useAppSelector } from "@/hooks/useAppSelector";
import { useAppDispatch } from "@/hooks/useAppDispatch";
import { setSelectedLock } from "@/app/store/locks/reducer";
import { NewLockHandbook } from "@/app/components/handbooks/lockHandbook";
import { skudLinks, Links } from "@/app/components/navigation/links/Links";
import { useLocation, useNavigate } from "react-router-dom";
import { NewRenterHandbook } from "@/app/components/handbooks/renterHandbook";
import { CodependentRenterEmployeeHandbook } from "@/app/components/handbooks/userHandbook";
import { LandlordHandbook } from "@/app/components/handbooks/landlordHandbook";

type IssuerType = "BUSINESS" | "RENTER";

export function IssueCardPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const selectedLockId = useAppSelector((state) => state.locks.selectedLockId);
  const businessProfileId = useAppSelector(
    (state) => state.businessProfile.businessProfile.id,
  );
  const locations = useAppSelector((state) => state.handbooks.location);

  const [devices, setDevices] = useState<LinkedDevice[] | null>(null);
  const [issuerType, setIssuerType] = useState<IssuerType>("BUSINESS");
  const [selectedRenter, setSelectedRenter] = useState<number | null>(null);
  const [selectedUser, setSelectedUser] = useState<number | null>(null);
  const [issuingCardForDevice, setIssuingCardForDevice] = useState<
    number | null
  >(null);

  const handleError = useErrorHandling();

  const handleUnlock = useCallback(() => {
    if (!selectedLockId) {
      toast.error("Выберите замок");
      return;
    }

    api.locks
      .unlock(selectedLockId)
      .then(() => toast.success("Замок успешно открыт!"))
      .catch((err) => {
        console.log(err);
        const { errorMessage } = handleError(err);
        toast.error(errorMessage);
      });
  }, [selectedLockId, handleError]);

  useEffect(() => {
    api.devices
      .list()
      .then((response) => {
        setDevices(response.data);
      })
      .catch((err) => {
        const { errorMessage } = handleError(err);
        toast.error(errorMessage);
      });
  }, [handleError]);

  const issueCard = useCallback(
    (deviceId: number) => {
      if (!selectedUser) {
        toast.error("Выберите пользователя");
        return;
      }

      setIssuingCardForDevice(deviceId);

      api.accessCards
        .create({
          device_id: deviceId,
          renter_id: issuerType === "RENTER" ? selectedRenter : null,
          owner_id: businessProfileId,
          user_id: selectedUser,
          issuer_type: issuerType,
        })
        .then((response) => {
          toast.success(response.message || "Карта успешно выдана");
          navigate("/dashboard/skud/access-cards");
        })
        .catch((err) => {
          const { errorMessage } = handleError(err);
          toast.error(errorMessage);
        })
        .finally(() => {
          setIssuingCardForDevice(null);
        });
    },
    [
      selectedUser,
      issuerType,
      selectedRenter,
      businessProfileId,
      handleError,
      navigate,
    ],
  );

  const getLocationName = (locationId: number) => {
    const location = locations.find((loc: any) => loc.id === locationId);
    return location?.display_name || `Локация #${locationId}`;
  };

  // Фильтруем устройства - пока считаем все linked устройства online
  const onlineDevices = devices || [];

  return (
    <>
      <div className="flex flex-col md:flex-row gap-4 w-full">
        <div className="flex flex-col gap-2 md:min-w-[240px] w-full md:w-auto">
          {/* @ts-ignore */}
          <NewLockHandbook
            errorMessage=""
            isInvalid={false}
            onSelectionChange={(key: any) =>
              dispatch(setSelectedLock(Number(key)))
            }
            selectedKey={selectedLockId ? String(selectedLockId) : undefined}
          />
          <Links
            links={skudLinks(location.pathname, !!selectedLockId, handleUnlock)}
          />
        </div>
        <div className="flex-grow flex flex-col gap-4">
          <div className="w-full flex flex-col gap-4 bg-white dark:bg-zinc-900 p-4 rounded-xl border-2 border-foreground-200 dark:border-foreground-100">
            <span className="font-medium text-lg">Выдача карты доступа</span>

            <div className="flex flex-col gap-4">
              <RadioGroup
                label="Тип выдачи"
                orientation="horizontal"
                value={issuerType}
                onValueChange={(value) => {
                  setIssuerType(value as IssuerType);
                  setSelectedRenter(null);
                  setSelectedUser(null);
                }}>
                <Radio value="BUSINESS">Бизнес</Radio>
                <Radio value="RENTER">Арендатор</Radio>
              </RadioGroup>

              <div className="flex flex-row gap-4 flex-wrap">
                {issuerType === "RENTER" && (
                  <div className="flex flex-col gap-2 min-w-[200px]">
                    {/* @ts-ignore */}
                    <NewRenterHandbook
                      label="Арендатор"
                      errorMessage=""
                      isInvalid={false}
                      onSelectionChange={(key: any) => {
                        setSelectedRenter(Number(key));
                        setSelectedUser(null);
                      }}
                      selectedKey={
                        selectedRenter ? String(selectedRenter) : undefined
                      }
                    />
                  </div>
                )}

                <div className="flex flex-col gap-2 min-w-[200px]">
                  {issuerType === "BUSINESS" ? (
                    // Для бизнеса - показываем ТОЛЬКО сотрудников бизнеса (landlords)
                    /* @ts-ignore */
                    <LandlordHandbook
                      label="Сотрудник"
                      errorMessage=""
                      isInvalid={false}
                      onSelectionChange={(key: any) =>
                        setSelectedUser(Number(key))
                      }
                      selectedKey={
                        selectedUser ? String(selectedUser) : undefined
                      }
                    />
                  ) : (
                    // Для рентера - показываем только сотрудников выбранного рентера
                    /* @ts-ignore */
                    <CodependentRenterEmployeeHandbook
                      label="Пользователь"
                      errorMessage=""
                      isInvalid={false}
                      deps={[selectedRenter ? String(selectedRenter) : ""]}
                      onSelectionChange={(key: any) =>
                        setSelectedUser(Number(key))
                      }
                      selectedKey={
                        selectedUser ? String(selectedUser) : undefined
                      }
                    />
                  )}
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <span className="font-medium">Выберите диспенсер:</span>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {onlineDevices.map((device) => (
                  <Card
                    key={device.id}
                    shadow="none"
                    className="border-2 border-foreground-200 dark:border-foreground-100">
                    <CardBody className="gap-2">
                      <div className="flex flex-col gap-1">
                        <span className="font-medium text-sm">
                          {device.name}
                        </span>
                        <span className="text-xs text-foreground-500">
                          ID: {device.device_id}
                        </span>
                        <span className="text-xs text-foreground-500">
                          {getLocationName(device.location_id)} | Этаж:{" "}
                          {device.floor}
                        </span>
                      </div>
                      <Button
                        size="sm"
                        color="primary"
                        isDisabled={!selectedUser}
                        isLoading={issuingCardForDevice === device.id}
                        onClick={() => issueCard(device.id)}>
                        Выдать карту
                      </Button>
                    </CardBody>
                  </Card>
                ))}
              </div>
              {onlineDevices.length === 0 && (
                <div className="flex flex-col items-center justify-center p-8">
                  <span className="text-foreground-500">
                    Нет доступных диспенсеров
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
